import { useI18n } from '../../../i18n';

interface LoadingSpinnerProps {
  /** Texto explícito. Si no se pasa, usa t.common.cargando del idioma activo. */
  text?: string;
  /** Preset de tamaño: sm=24px, md=40px, lg=64px */
  size?: 'sm' | 'md' | 'lg';
  /** Centra el spinner en overlay absoluto (position: absolute, inset:0) */
  fullPage?: boolean;
  /** Renderiza en línea (horizontal) para filas, badges y botones */
  inline?: boolean;
  /** Texto en gris claro — para paneles y bloques secundarios */
  muted?: boolean;
}

const SIZES = { sm: 24, md: 40, lg: 64 } as const;

export function LoadingSpinner({
  text,
  size = 'md',
  fullPage = false,
  inline = false,
  muted = false,
}: LoadingSpinnerProps) {
  const { t } = useI18n();
  const px = SIZES[size];
  const label = text ?? t.common.cargando ?? 'Cargando...';

  const spinner = (
    <img
      src="/img/Imagotipo-Color.png"
      alt={label}
      style={{
        width: px,
        height: px,
        animation: 'viaSpinPulse 2.5s cubic-bezier(0.4,0,0.2,1) infinite',
        flexShrink: 0,
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).src = '/img/Imagotipo-blanco.png';
      }}
    />
  );

  if (inline) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {spinner}
        <span
          style={{
            fontSize: size === 'sm' ? 11 : 13,
            color: muted ? '#9CA3AF' : '#595959',
            fontFamily: 'inherit',
          }}
        >
          {label}
        </span>
        <style>{`@keyframes viaSpinPulse{0%{transform:rotate(0deg);opacity:1}50%{opacity:.5}100%{transform:rotate(360deg);opacity:1}}`}</style>
      </span>
    );
  }

  const content = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: size === 'sm' ? 4 : 8,
        padding: fullPage ? 0 : size === 'sm' ? 12 : size === 'md' ? 24 : 40,
      }}
    >
      {spinner}
      <span
        style={{
          fontSize: size === 'sm' ? 10 : size === 'md' ? 12 : 14,
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 500,
          color: muted ? '#9CA3AF' : '#595959',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </span>
      <style>{`@keyframes viaSpinPulse{0%{transform:rotate(0deg);opacity:1}50%{opacity:.5}100%{transform:rotate(360deg);opacity:1}}`}</style>
    </div>
  );

  if (fullPage) {
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          background: 'transparent',
        }}
      >
        {content}
      </div>
    );
  }

  return content;
}
